import { createFileRoute } from "@tanstack/react-router";
import { LivePulse, SectionHeader, StatCard, AuctionCardSkeleton } from "@/components/ui";

// ─── Custom GoT Components ───────────────────────────────────────────────────

function NobleButton({
  children,
  variant = "primary",
  className = "",
  ...props
}: {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const baseStyles =
    "font-epic tracking-[0.2em] uppercase px-10 h-12 text-sm transition-all active:scale-[0.98]";
  const variants = {
    primary:
      "bg-primary text-white shadow-[0_0_20px_rgba(205,1,1,0.3)] hover:shadow-primary/50 hover:brightness-110",
    secondary:
      "bg-secondary text-iron-900 shadow-[0_0_20px_rgba(241,198,41,0.2)] hover:shadow-secondary/40 hover:brightness-110",
    outline: "border border-iron-500 text-iron-300 hover:bg-iron-800/50",
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

function GreatHouseCard({
  children,
  title,
  subtitle,
  price,
  borderSide = "left",
  className = "",
}: {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  price?: string;
  borderSide?: "left" | "all";
  className?: string;
}) {
  const borderStyles =
    borderSide === "left"
      ? "border-l-[6px] border-primary"
      : "border border-iron-800";

  return (
    <div
      className={`bg-iron-900 ${borderStyles} group relative overflow-hidden p-8 shadow-2xl ${className}`}
    >
      <div className="mb-6 flex items-start justify-between">
        <div>
          {subtitle && (
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              {subtitle}
            </span>
          )}
          <h4 className="font-epic text-3xl tracking-widest text-iron-100">
            {title}
          </h4>
        </div>
        {price && (
          <span className="font-epic text-sm font-bold text-secondary">
            {price}
          </span>
        )}
      </div>
      <div className="mb-6 h-px bg-iron-800/50" />
      {children}
    </div>
  );
}

function HeraldicChip({
  children,
  variant = "iron",
}: {
  children: React.ReactNode;
  variant?: "iron" | "gold" | "blood";
}) {
  const variants = {
    iron: "bg-iron-800 text-iron-300 border-iron-700",
    gold: "bg-secondary/10 text-secondary border-secondary/20",
    blood: "bg-primary/10 text-primary border-primary/20",
  };
  return (
    <span
      className={`inline-block border px-3 py-1 text-[10px] uppercase tracking-widest ${variants[variant]}`}
    >
      {children}
    </span>
  );
}

function ScrollInput({
  label,
  placeholder,
}: {
  label: string;
  placeholder: string;
}) {
  return (
    <div className="group flex flex-col gap-2">
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-iron-400 transition-colors group-focus-within:text-secondary">
        {label}
      </label>
      <input
        placeholder={placeholder}
        className="border-b border-iron-800 bg-transparent py-2 text-iron-100 transition-colors placeholder:text-iron-700 focus:border-secondary focus:outline-none"
      />
    </div>
  );
}

// ─── Design System Page ──────────────────────────────────────────────────────

export const Route = createFileRoute("/ds")({
  component: DesignSystemPage,
});

function DesignSystemPage() {
  const primaryColors = [
    {
      name: "Stony Charcoal (Core)",
      hex: "#13171F",
      class: "bg-[#13171F] border border-iron-500",
    },
    {
      name: "Lannister Red",
      hex: "#a32927",
      class: "bg-[#a32927] text-white shadow-[inset_0_0_15px_rgba(0,0,0,0.5)]",
    },
    {
      name: "Stark Grey",
      hex: "#4D495B",
      class: "bg-[#4D495B] text-white shadow-[inset_0_0_10px_rgba(0,0,0,0.2)]",
    },
    {
      name: "Baratheon Gold",
      hex: "#F1C629",
      class:
        "bg-[#F1C629] text-[#13171F] shadow-[inset_0_0_15px_rgba(241,198,41,0.2)]",
    },
    {
      name: "Fallen Snow",
      hex: "#EAEBEB",
      class: "bg-iron-100 text-iron-900 border border-iron-300",
    },
  ];

  return (
    <div className="min-h-screen bg-background font-sans text-foreground selection:bg-secondary/30 selection:text-secondary">
      <div className="container mx-auto max-w-6xl px-8 py-20">
        {/* Heraldic Header */}
        <header className="mb-24 flex flex-col items-center text-center">
          <div className="mb-8 h-16 w-1 bg-secondary shadow-[0_0_15px_rgba(241,198,41,0.5)]" />
          <h1 className="mb-4 text-7xl font-medium tracking-[0.4em] text-iron-100">
            The Great Ledger
          </h1>
          <div className="flex w-full items-center justify-center gap-4">
            <div className="h-px grow bg-iron-800" />
            <p className="shrink-0 text-sm font-bold uppercase tracking-[0.3em] text-secondary">
              Auction of the Houses
            </p>
            <div className="h-px grow bg-iron-800" />
          </div>
          <p className="mt-8 border border-iron-800 px-4 py-2 text-xs font-bold uppercase italic tracking-[0.2em] text-iron-500">
            Local UI Primitives • Pure Tailwind Engine
          </p>
        </header>

        {/* Great Palette */}
        <section className="mb-32">
          <div className="mb-12 flex items-center gap-6">
            <h2 className="font-epic text-2xl uppercase tracking-widest text-iron-200">
              The Royal Colors
            </h2>
            <div className="h-px grow bg-iron-800" />
          </div>
          <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-2 lg:grid-cols-5">
            {primaryColors.map((color) => (
              <div key={color.name} className="flex flex-col gap-3">
                <div
                  className={`${color.class} flex h-32 items-center justify-center`}
                >
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] mix-blend-difference">
                    {color.hex}
                  </span>
                </div>
                <span className="font-epic text-[10px] uppercase tracking-widest text-iron-400">
                  {color.name}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Status Indicators */}
        <section className="mb-32">
          <div className="mb-12 flex items-center gap-6">
            <h2 className="font-epic text-2xl uppercase tracking-widest text-iron-200">
              Status Indicators
            </h2>
            <div className="h-px grow bg-iron-800" />
          </div>
          <div className="mb-8 flex items-center gap-6">
            <LivePulse variant="blood" size="md" />
            <span className="font-body text-iron-300">Live Active</span>
          </div>
          <div className="mb-8 flex items-center gap-6">
            <LivePulse variant="gold" size="md" />
            <span className="font-body text-iron-300">Auction Pending</span>
          </div>
          <div className="mb-8 flex items-center gap-6">
            <LivePulse variant="steel" size="sm" />
            <span className="font-body text-iron-300">Ended</span>
          </div>
          <div className="mt-8">
            <SectionHeader title="Active Listings" trailing="42 items" />
          </div>
        </section>

        {/* Royal Scribes */}
        <section className="mb-32">
          <div className="mb-12 flex items-center gap-6">
            <h2 className="font-epic text-2xl uppercase tracking-widest text-iron-200">
              The Royal Scribes
            </h2>
            <div className="h-px grow bg-iron-800" />
          </div>
          <div className="grid grid-cols-1 items-start gap-16 lg:grid-cols-2">
            <div className="group space-y-10">
              <div>
                <span className="mb-4 block text-[10px] font-bold uppercase tracking-[0.3em] text-iron-400 underline decoration-2 underline-offset-8">
                  Epic Display
                </span>
                <h1 className="mb-6 text-6xl text-iron-100 transition-colors group-hover:text-white">
                  Hear My Roar
                </h1>
                <h2 className="mb-4 text-4xl tracking-[0.2em] text-iron-300">
                  Winter Is Coming
                </h2>
                <h3 className="font-body text-2xl italic text-iron-400">
                  Fire and Blood
                </h3>
              </div>
            </div>
            <div className="space-y-8 border border-iron-800/50 bg-iron-900 p-10 backdrop-blur-md">
              <span className="mb-4 block text-[10px] font-bold uppercase leading-none tracking-[0.3em] text-secondary">
                The Decree
              </span>
              <p className="font-body text-xl leading-relaxed text-iron-200">
                The iron-born do not sow. They take what they desire. In the
                halls of the Great Sept, where the ancestors watch from the
                shadows, every word is a bond, every coin a life, and every
                auction a battlefield.
              </p>
              <p className="lore-text text-lg leading-relaxed text-iron-400/80">
                "Smallfolk buy and sell with copper. Kings buy and sell with
                blood and steel."
              </p>
            </div>
          </div>
        </section>

        {/* War Room Tools */}
        <section className="mb-32">
          <div className="mb-12 flex items-center gap-6">
            <h2 className="font-epic text-2xl uppercase tracking-widest text-iron-200">
              Forge & Steel
            </h2>
            <div className="h-px grow bg-iron-800" />
          </div>

          <div className="grid grid-cols-1 gap-20 md:grid-cols-2">
            {/* Loading Skeletons */}
            <div className="space-y-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-iron-500">
                Loading States
              </h3>
              <AuctionCardSkeleton />
            </div>

            {/* Stat Cards */}
            <div className="space-y-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-iron-500">
                Stat Cards
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <StatCard label="Total Bids" value="1,247" />
                <StatCard label="Active" value="42" accent />
              </div>
            </div>

            {/* Command Actions */}
            <div className="space-y-16">
              <div className="space-y-8">
                <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-iron-500">
                  Battle Orders
                </h3>
                <div className="flex flex-wrap gap-6">
                  <NobleButton variant="primary">Seize Assets</NobleButton>
                  <NobleButton variant="secondary">Pay Debt</NobleButton>
                  <NobleButton variant="outline">Enter Pit</NobleButton>
                </div>
              </div>

              <div className="space-y-8">
                <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-iron-500">
                  Scrolls of Intake
                </h3>
                <div className="max-w-sm space-y-8">
                  <ScrollInput
                    label="House Representative"
                    placeholder="e.g. Tyrion of Lannister"
                  />
                  <ScrollInput
                    label="Target Kingdom"
                    placeholder="The Iron Islands"
                  />
                </div>
              </div>
            </div>

            {/* Noble Cards */}
            <div className="space-y-10">
              <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-iron-500">
                Great Relics
              </h3>

              <div className="space-y-8">
                {/* Lannister Theme */}
                <GreatHouseCard
                  title="Lion's Tooth"
                  subtitle="Lannister Tier"
                  price="2,500 RUPIAH"
                >
                  <p className="mb-6 font-body text-sm leading-relaxed text-iron-400">
                    A gilded hilt crafted from the pure sunlight of the
                    Westerlands. Its bite is as sharp as a debt unpaid.
                  </p>
                  <div className="flex gap-3">
                    <HeraldicChip>Valyrian Steel</HeraldicChip>
                    <HeraldicChip variant="gold">One-Handed</HeraldicChip>
                  </div>
                </GreatHouseCard>

                {/* Obsidian Theme */}
                <GreatHouseCard
                  title="Dragonstone Shard"
                  subtitle="Ancient Heirloom"
                  borderSide="all"
                  className="border-secondary/20 bg-black/40"
                >
                  <p className="mb-8 font-body text-sm leading-relaxed text-iron-400">
                    A raw shard of obsidian found in the depths of Dragonstone.
                    It hums with the memory of old fire.
                  </p>
                  <NobleButton variant="secondary" className="w-full">
                    Place Royal Bid
                  </NobleButton>
                </GreatHouseCard>
              </div>
            </div>
          </div>
        </section>

        {/* Constraints Footer */}
        <footer className="mt-40 flex flex-col items-center border-t border-iron-800 pt-16">
          <div className="flex gap-12 text-[10px] font-bold uppercase tracking-[0.4em] text-iron-500">
            <span className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-secondary shadow-[0_0_5px_#F1C629]" />{" "}
              No Gradients
            </span>
            <span className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-primary shadow-[0_0_5px_#a32927]" />{" "}
              Pure Tailwind
            </span>
            <span className="flex items-center gap-3">
              <span className="h-1.5 w-1.5 bg-iron-300 shadow-[0_0_5px_#ACB1B2]" />{" "}
              Local Primitives
            </span>
          </div>
          <p className="mt-8 font-epic text-[9px] uppercase italic tracking-[0.5em] tracking-tighter text-iron-700">
            Obsidian • Blood • Gold
          </p>
        </footer>
      </div>
    </div>
  );
}
